import { useEffect, useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { afterTransition } from '../../hooks/gestures/afterTransition'
import { EDGE_ZONE } from '../../hooks/gestures/recognisers'
import { usePointerGestures } from '../../hooks/gestures/usePointerGestures'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { MOBILE_QUERY } from '../../lib/platform'
import { useBack } from './useBack'

/**
 * Phones: swipe from the left edge of a conversation back to the list (spec §3.4). iPhone Home Screen apps have no
 * back gesture of their own; Android's comes first where the phone has one. While the pane has data-edge-back the
 * list shows under it and it follows the finger; letting go past a third of the width, or a quick flick, goes back,
 * otherwise it springs back. The pane is styled directly, so a drag doesn't re-render the app.
 */
export function useEdgeSwipeBack() {
  const { pathname } = useLocation()
  const back = useBack()
  const phone = useMediaQuery(MOBILE_QUERY)
  const paneRef = useRef<HTMLElement>(null)
  const settle = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => () => clearTimeout(settle.current), [])

  // Going back puts the pane back in the same commit as the route change, so it never shows in place in between. Any
  // navigation does: one that lands while the pane settles after a swipe wins over going back.
  useLayoutEffect(() => {
    clearTimeout(settle.current)
    const pane = paneRef.current
    if (!pane) return
    delete pane.dataset.edgeBack
    pane.style.transform = ''
  }, [pathname])

  const { handlers } = usePointerGestures({
    swipe:
      phone && pathname.startsWith('/c/')
        ? {
            direction: 'right',
            startX: { max: EDGE_ZONE },
            mark: () => (paneRef.current?.clientWidth || window.innerWidth) / 3,
            flick: true,
            onStart: () => {
              clearTimeout(settle.current)
              if (paneRef.current) paneRef.current.dataset.edgeBack = 'dragging'
            },
            onMove: ({ offset }) => {
              if (paneRef.current) paneRef.current.style.transform = `translateX(${offset}px)`
            },
            onEnd: ({ committed }) => {
              const pane = paneRef.current
              if (!pane) return
              pane.dataset.edgeBack = 'settling'
              pane.style.transform = committed ? 'translateX(100%)' : 'translateX(0px)'
              settle.current = afterTransition(pane, () => {
                if (committed) {
                  back.go()
                } else {
                  delete pane.dataset.edgeBack
                  pane.style.transform = ''
                }
              })
            },
          }
        : null,
  })

  return { paneRef, handlers }
}
